import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { Box, Button, Card, Fade, Typography, styled } from "@mui/material";

import { Page } from "../components/layout/Page";
import { Patient } from "../models/patient.ts";
import { PatientBackCta } from "../components/PatientBackCta.tsx";
import { PatientBody } from "../components/PatientBody.tsx";
import { PatientInfoDialog } from "../components/PatientInfoDialog.tsx";
import { PatientMenu } from "../components/PatientMenu.tsx";
import { PatientsList } from "../components/PatientsList.tsx";
import {
  HorizontalStack,
  VerticalStack,
} from "../components/primitives/Stacks.tsx";
import { useAllPatientData, usePatient } from "../queries/patients-query";
import { useAppContext } from "../context/AppContext.tsx";
import { usePatientData } from "../hooks/usePatientData.ts";
import { DateTime } from "../components/primitives/DateTime.tsx";
import DeleteExportDialog from "../components/DeleteExportDialog.tsx";

const Side = styled(Box)(({ theme }) => ({
  display: "flex",
  flexBasis: "340px",
  flexDirection: "column",
  flex: "0 0 340px",
  gap: theme.spacing(2),
  position: "relative",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Main = styled(Box)(() => ({
  flex: "1 1 auto",
  position: "relative",
  width: "100%",
}));

export const PatientPage: React.FC<{ patient: Patient }> = ({ patient }) => {
  const { t } = useTranslation();
  const { dataConfig, setDataConfig, togglePatientMenu } = useAppContext();

  const patientData = usePatientData({
    exportId: dataConfig.exportId || undefined,
    patientId: dataConfig.patientId || undefined,
  });

  useEffect(() => {
    return () => {
      setDataConfig({ patientId: undefined, exportId: undefined });
    };
  }, [setDataConfig]);

  return (
    <>
      <Page
        BodyProps={{ sx: { overflow: "hidden" } }}
        HeadProps={{
          allowBack: true,
          titleContent: (
            <Button
              component={VerticalStack}
              spacing={0}
              onClick={(e) =>
                togglePatientMenu({
                  anchorEl: e.currentTarget,
                  displayImports: true,
                  patient,
                })
              }
              sx={{
                "&, &:hover": { boxShadow: "none" },
                display: { md: "none" },
              }}
            >
              <Typography
                align="center"
                component="span"
                display="block"
                noWrap
                sx={{ lineHeight: 1 }}
                variant="subtitle1"
              >
                {patient.name}
              </Typography>
              <Fade in={Boolean(patientData)} key={dataConfig.exportId}>
                <HorizontalStack
                  style={{ opacity: 0.66 }}
                  alignItems="center"
                  spacing={0.5}
                >
                  <Typography align="center" noWrap variant="caption">
                    {t("patient.export-date")}
                  </Typography>
                  <Typography align="center" noWrap variant="caption">
                    <DateTime timestamp={patientData?.exportDate.getTime()} />
                  </Typography>
                </HorizontalStack>
              </Fade>
            </Button>
          ),
        }}
        MainProps={{
          sx: {
            display: "flex",
            flexDirection: "row",
            height: "100%",
            overflow: "hidden",
          },
        }}
      >
        <Side>
          <PatientBackCta sx={{ flexShrink: 0 }} />
          <PatientsList
            sx={{
              height: "100%",
              mx: -0.5,
              overflow: "auto",
              position: "relative",
              px: 0.5,
              width: "340px",
            }}
          />
        </Side>
        <Main>
          <Fade appear in key={`${patient.id}${dataConfig.exportId}`}>
            <Card
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              <PatientBody patient={patient} />
            </Card>
          </Fade>
        </Main>
      </Page>
      <PatientInfoDialog />
      <PatientMenu />
      <DeleteExportDialog />
    </>
  );
};

const ConnectedPatientPage: React.FC = () => {
  const navigate = useNavigate();

  const { setDataConfig } = useAppContext();

  const { id: patientId } = useParams<{ id: string }>();
  const { data: patient } = usePatient(patientId);
  const { data: exports } = useAllPatientData(patientId);

  useEffect(() => {
    if (!patient) navigate("/protected/all-patients");
  }, [patient]);

  useEffect(() => {
    if (!patient) return;
    const latest = exports?.sort(
      (a, b) => b.exportDate.getTime() - a.exportDate.getTime(),
    )[0];
    setDataConfig({ patientId: patient.id, exportId: latest?.exportId });
  }, [patient, setDataConfig]);

  if (!patient) return;

  return <PatientPage patient={patient} />;
};

export default ConnectedPatientPage;
